import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  ViewChild,
  ElementRef,
  Renderer2,
  ChangeDetectionStrategy
} from '@angular/core';
import { UtilsService } from 'src/app/shared/utils.service';
import { FileManagerComponent } from '../file-manager/file-manager.component';

@Component({
  selector: 'app-file-browse-button',
  templateUrl: './file-browse-button.component.html',
  styleUrls: ['./file-browse-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class FileBrowseButtonComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef;

  // Parent object type and id to handle file uploads.
  @Input('parentObjectType') parentObjectType: string = '';
  @Input('parentObjectId') parentObjectId: number = 0;
  @Input('parentRemove') parentRemove: boolean = false; // The parent component will handle removing files.
  @Input('parentUpload') parentUpload: boolean = false; // The parent component will handle uploading files.
  @Input('getSelectedFiles') getSelectedFiles: boolean = false; // Get the selected files

  // Input for a file count.
  @Input('filesCount') filesCount: number = 0;

  // Event that gets triggered when files are selected.
  @Output() selectedFiles = new EventEmitter<FileList>();

  // Event that gets triggered when the dialog closes.
  @Output() afterDialogClosedEvent = new EventEmitter<FileList>();

  // Event that gets triggered when a file removed.
  @Output() afterFileRemove = new EventEmitter<FileList>();

  // Enable/Disable the button.
  @Input('disabled') disabled: boolean = false;

  constructor(public utils: UtilsService, public renderer: Renderer2) {}

  ngOnInit(): void {}

  /**
   * Send the selected files back to the caller, holding back any that are over the size limit.
   *
   * The size check lives here because this is the single point where a user picks files for every
   * upload in the portal, so one check covers all ~30 consumers of this button. Previously nothing
   * checked size anywhere on the client: an oversized selection was uploaded, rejected by the API,
   * and the failure was never shown. The limit was advertised only as tooltip prose.
   *
   * Oversized files are dropped rather than failing the whole selection, so a user who picked six
   * files and one large one still gets the other five uploaded, and is told which one was left out.
   *
   * @param {any} event Event object to extract files from.
   * @return {void}
   */
  onSelectFiles(event: any): void {
    const input: HTMLInputElement = event?.target;
    const picked: File[] = Array.from<File>(input?.files || []);
    const { allowed, rejected } = this.utils.checkFileSizes(picked);

    if ( rejected.length > 0 ) {
      this.utils.showModal('File Too Large', this.utils.getFilesTooLargeMessage(rejected));
    }

    if ( allowed.length > 0 ) {
      this.filesCount = allowed.length;
      // When nothing was held back, pass the browser's own FileList straight through. This is the
      // common path and leaves existing behaviour byte for byte identical.
      this.selectedFiles.emit(
        rejected.length === 0 ? input.files : this.toFileList(allowed)
      );
    } else {
      this.filesCount = 0;
    }

    // Cleared last, after the files have been read and emitted, because assigning value empties the
    // input's FileList. Clearing matters so that picking the same path again still raises a change
    // event: without it, a user who is told their file is too large, shrinks it, and reselects the
    // same filename would get no response at all, as the input's value never changed.
    if ( input ) {
      input.value = '';
    }
  }

  /**
   * Rebuilds a FileList from a filtered set of files.
   *
   * Consumers of the selectedFiles output are typed against FileList, so the contract is kept rather
   * than switched to an array.
   *
   * @param {File[]} files The files to emit.
   * @return {FileList} A FileList containing exactly the given files.
   */
  private toFileList(files: File[]): FileList {
    // DataTransfer is the only way to build a real FileList. Guarded because it is not constructible
    // in every environment (notably jsdom), and an array is close enough for every consumer: they all
    // read it with Array.from() or a spread.
    try {
      const transfer: DataTransfer = new DataTransfer();
      files.forEach((file: File): void => {
        transfer.items.add(file);
      });
      return transfer.files;
    } catch (err) {
      return files as unknown as FileList;
    }
  }

  /**
   * Trigger the file input event.
   * @param {Event} evt - The event that triggered the file selector
   * @return {void}
   */
  onTriggerFileSelector(evt: Event): void {
    evt.stopPropagation();
    evt.preventDefault();
    if (this.fileInput) {
      const clickEvt: MouseEvent = new MouseEvent('click', { bubbles: true });
      this.fileInput.nativeElement.dispatchEvent(clickEvt);
    }
  }

  /**
   * Open the file manager dialog to handle file uploads.
   */
  onManageFiles(): void {
    this.utils.showComponentDialog(
      FileManagerComponent,
      {
        parentObjectType: this.parentObjectType,
        parentObjectId: this.parentObjectId,
        parentRemove: this.parentRemove,
        parentUpload: this.parentUpload,
        getSelectedFiles: this.getSelectedFiles
      },
      {
        width: '1200px'
      },
      (result: any): void => {
        if (result > 0 && this.parentRemove) {
          this.afterFileRemove.emit(result);
        } else if (result && result.length > 0 && this.parentUpload) {
          this.selectedFiles.emit(result);
        } else if (result && result.length > 0 && this.getSelectedFiles) {
          this.selectedFiles.emit(result);
          this.afterDialogClosedEvent.emit(result);
        } else {
          this.afterDialogClosedEvent.emit(null);
        }
      }
    );
  }
}
